import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, buildAudioUrl } from "../lib/api";
import { Volume2, Coins, Rabbit, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const CURRENCY_ORDER = ["free_currency", "paid_currency", "giveaway_currency", "event_currency"];

function currencyLabel(currency) {
  if (currency?.name) return currency.name;
  const raw = currency?.kind || currency?.id || "Currency";
  return raw.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
}

function visibleCurrencies(currencies) {
  return [...(currencies || [])]
    .filter((c) => c.enabled !== false && c.visible !== false)
    .sort((a, b) => {
      const ai = CURRENCY_ORDER.indexOf(a.id);
      const bi = CURRENCY_ORDER.indexOf(b.id);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });
}

export default function Viewer() {
  const { login } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    const load = async () => {
      const { data } = await api.get(`/viewer/${login}`);
      setData(data);
    };
    load();
    const id = setInterval(load, 8000);
    return () => clearInterval(id);
  }, [login]);

  if (!data) return <div className="p-10 text-sm">Loading...</div>;

  return (
    <div className="min-h-screen grid-bg bg-noise">
      <div className="relative max-w-5xl mx-auto px-6 py-12">
        <div className="flex items-center gap-2 mb-10">
          <div className="w-9 h-9 rounded bg-[hsl(var(--primary))] flex items-center justify-center"><Rabbit className="w-5 h-5 text-white" /></div>
          <span className="text-lg font-semibold tracking-tight">TheBarracksBunny AI</span>
          {data.streamer && (
            <span className="ml-3 flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]">
              {data.streamer.profile_image_url && <img src={data.streamer.profile_image_url} alt="" className="w-5 h-5 rounded-full" />}
              live for {data.streamer.display_name}
            </span>
          )}
        </div>

        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="label-eyebrow">Viewer profile</div>
            <h1 className="text-4xl font-bold tracking-tight mt-1">{data.viewer.display_name}</h1>
            <div className="font-mono text-sm text-[hsl(var(--muted-foreground))] mt-1">@{data.viewer.login}  -  {data.viewer.watchtime_minutes}m active watchtime</div>
          </div>
          <a href="/interact" className="text-xs uppercase tracking-widest px-4 py-2 rounded border border-[hsl(var(--primary))] text-[hsl(var(--primary))] flex items-center gap-2" data-testid="open-interact">
            <ExternalLink className="w-3 h-3" /> Open Rat Interactive
          </a>
        </div>

        <div className="mt-8 flex flex-wrap justify-center gap-3">
          {visibleCurrencies(data.currencies).map(c => (
            <div key={c.id} className="surface p-5 w-full sm:w-[calc(50%-0.375rem)] lg:w-[calc(25%-0.6rem)] min-h-32">
              <div className="label-eyebrow" style={{ color: c.color }}>{currencyLabel(c)}</div>
              <div className="mt-3 text-3xl font-bold font-mono" style={{ color: c.color }}>{data.viewer.balances[c.id] || 0}</div>
            </div>
          ))}
        </div>

        <div className="mt-12">
          <div className="flex items-center gap-2"><Volume2 className="w-5 h-5 text-[hsl(var(--primary))]" /><h2 className="text-xl font-semibold">Quick redeem</h2></div>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Use <code className="font-mono text-white">!redeem &lt;key&gt;</code> in chat - or open <a className="underline text-[hsl(var(--primary))]" href="/interact">Rat Interactive</a> to sign in and click-to-redeem.</p>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="public-sounds-list">
            {data.sounds.length === 0 && <div className="surface p-5 text-sm text-[hsl(var(--muted-foreground))]">No interactions yet - check back soon.</div>}
            {data.sounds.map(s => {
              const cur = data.currencies.find(c => c.id === s.currency_id);
              return (
                <div key={s.id} className="surface p-4 flex flex-col">
                  <div className="flex items-center justify-between">
                    <div className="font-medium">{s.name}</div>
                    <button onClick={() => new Audio(buildAudioUrl(s)).play().catch(() => toast.error("Cannot play"))} className="p-1.5 text-[hsl(var(--primary))]"><Volume2 className="w-4 h-4" /></button>
                  </div>
                  <code className="text-xs font-mono text-[hsl(var(--muted-foreground))] mt-1">!redeem {s.key}</code>
                  <div className="mt-3 flex items-center gap-2 text-sm">
                    <Coins className="w-4 h-4" style={{ color: cur?.color }} />
                    <span className="font-mono" style={{ color: cur?.color }}>{s.cost}</span>
                    <span className="text-[hsl(var(--muted-foreground))]">{cur?.name}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
